// -----------------------------------------------------------------------------
// src/CreatorUser/CreatorPages/Dashboard/creatorcomponents/CreatorCampaignPitchedDialog.jsx
// Redesigned with modern UI, proper mobile handling, and full button visibility
// -----------------------------------------------------------------------------
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Stack,
  Chip,
  Card,
  Link,
  useMediaQuery,
  IconButton,
  Collapse,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import client from "../../../../API";
import { useCreatorAuth } from "../../../../Hooks/creator-use-auth";
import useAlertDialog from "../../../../Components/useAlertDialog";
import AlertDialog from "../../../../Components/AlertDialog";
import useIsDesktop from "../../../../Hooks/useIsDesktop";
import CreatorNegotiateDialog from "./creatorcounterDialog";
import CreatorCampaignConversation from "./conversation";

/* ───────── constants ───────── */
const APP_SHELL_HEIGHT = 86;
const EXTRA_GUTTER = 12;
const MOBILE_BOTTOM_PADDING = APP_SHELL_HEIGHT + EXTRA_GUTTER + 20; // Extra 20px for breathing room

/* ───────── helpers ───────── */
const formatIdealDueDate = (dStr) => {
  const d = new Date(dStr);
  return isNaN(d)
    ? "Invalid Date"
    : `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear().toString().slice(-2)}`;
};

const renderBriefWithLinks = (txt = "") => {
  const url = /(https?:\/\/[^\s]+)/g;
  return txt.split(url).map((part, i) =>
    url.test(part) ? (
      <Link key={i} href={part} target="_blank" rel="noopener" sx={{ color: 'primary.main', fontWeight: 600 }}>
        {part}
      </Link>
    ) : (
      part
    )
  );
};

export default function CreatorCampaignPitchedDialog({
  openDialog,
  handleCloseDialog,
  campaignId,
}) {
  const [campaign, setCampaign] = useState(null);
  const [openNegotiateDialog, setOpenNegotiateDialog] = useState(false);
  const [openChatPopup, setOpenChatPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    brief: true,
    promotions: true,
    creatorBrief: true,
  });
  const editedEvents = useRef(false);

  const { creatorToken } = useCreatorAuth();
  const isDesktop = useIsDesktop();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { dialogState, openDialog: openAlert, closeDialog } = useAlertDialog();

  const handleCloseDialogRef = useRef(handleCloseDialog);
  handleCloseDialogRef.current = handleCloseDialog;

  /* ── fetch (deps exclude handleCloseDialog — parent passed inline fn caused infinite refetch) ── */
  useEffect(() => {
    if (!openDialog) return;
    let cancelled = false;
    setIsLoading(true);
    (async () => {
      try {
        const data = await client.campaigns.fetch(campaignId);
        if (cancelled) return;
        editedEvents.current = false;
        setCampaign(data);
        setIsLoading(false);
      } catch (err) {
        if (!cancelled) console.error(err);
        handleCloseDialogRef.current?.();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [openDialog, campaignId]);

  /* ── derived ── */
  const creator = useMemo(
    () => campaign?.creators.find((c) => c.id === creatorToken.creator_user.username),
    [campaign, creatorToken.creator_user.username]
  );
  const currentRate = creator?.agencyRate || creator?.price || 0;
  const pendingCounterAmount =
    creator?.pending_counter_offer ?? creator?.originalCounterOffer ?? null;
  const videoList = useMemo(() => creator?.videos.filter((v) => !v.parentId) || [], [creator]);
  const crosspostList = useMemo(() => creator?.videos.filter((v) => v.parentId) || [], [creator]);

  const refreshCampaign = async () => {
    try {
      const data = await client.campaigns.fetch(campaignId);
      setCampaign(data);
    } catch (err) {
      console.error("Error refreshing campaign:", err);
    }
  };

  /* ── accept / reject ── */
  const { mutate: updateStatus, isLoading: updatingStatus } = useMutation(
    (input) => client.creatorConnect.campaignUpdateStatus(campaignId, input),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["campaigns", "creator", creatorToken.creator_user.username]);
        handleCloseDialog();
        navigate("/creatorconnect/collabs", { replace: true });
      },
    }
  );

  const handleChangeStatus = (accepted) => {
    closeDialog();
    updateStatus(
      { status: accepted ? "Accepted" : "Declined" },
      {
        onSuccess: () =>
          openAlert(
            accepted ? "Accepted Campaign" : "Rejected Campaign",
            accepted
              ? "You have accepted to take part in this campaign. Good luck!"
              : "You have decided to reject this campaign. We hope to send campaigns more relevant to you in the future",
            closeDialog,
            null,
            "Ok"
          ),
        onError: (e) =>
          openAlert(
            "Error",
            `There was an error while changing your status: ${e.response?.data?.error || e.message}`,
            closeDialog,
            null,
            "Ok"
          ),
      }
    );
  };

  const onClickOption = (accepted) =>
    openAlert(
      accepted ? "Accept Campaign" : "Reject Campaign",
      accepted
        ? "You'll accept this campaign, be notified of upcoming due dates and receive messages from the campaign manager. Continue?"
        : "You'll reject this campaign. This action is not reversible, and the campaign will disappear from your dashboard. Continue?",
      () => handleChangeStatus(accepted),
      closeDialog,
      "Continue",
      "Cancel"
    );

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  /* ── UI ── */
  return (
    <>
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            margin: isMobile ? 0 : 2,
            height: isMobile ? '100vh' : 'auto',
            maxHeight: isMobile ? '100vh' : '92vh',
            borderRadius: isMobile ? 0 : 2,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }
        }}
        PaperProps={{
          elevation: isMobile ? 0 : 8,
          sx: {
            backgroundColor: theme.palette.background.default,
          }
        }}
      >
        {(isLoading || updatingStatus) ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flex: 1,
              minHeight: 400,
              background: `linear-gradient(135deg, ${theme.palette.background.default}, ${theme.palette.background.paper})`,
            }}
          >
            <Stack spacing={3} alignItems="center">
              <CircularProgress size={56} thickness={4.5} />
              <Typography variant="h6" color="text.secondary" fontWeight={600}>
                {isLoading ? 'Loading campaign details...' : 'Updating your response...'}
              </Typography>
            </Stack>
          </Box>
        ) : (
          <>
            {/* Header */}
            <Paper
              elevation={0}
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                borderRadius: 0,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                color: 'white',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, fontSize: isMobile ? '1.25rem' : '1.5rem' }}>
                  Campaign Offer
                </Typography>
                <IconButton onClick={handleCloseDialog} sx={{ color: 'white' }} size="large">
                  <CloseIcon />
                </IconButton>
              </Box>
            </Paper>

            {/* Scrollable Content */}
            <DialogContent
              sx={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                px: isMobile ? 2 : 3,
                py: 3,
                pb: isMobile ? `${MOBILE_BOTTOM_PADDING}px` : 3,
                WebkitOverflowScrolling: 'touch',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: theme.palette.grey[100],
                },
                '&::-webkit-scrollbar-thumb': {
                  background: theme.palette.grey[400],
                  borderRadius: '4px',
                },
              }}
            >
              <Stack spacing={3}>
                {/* Campaign Header Card */}
                <Card
                  elevation={0}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.grey[50]})`,
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ p: 3 }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontSize: isMobile ? '1.5rem' : '1.75rem',
                        fontWeight: 800,
                        mb: 1.5,
                        color: theme.palette.primary.main,
                      }}
                    >
                      {campaign.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={`Due: ${formatIdealDueDate(campaign.ideal_duedate)}`}
                        color="primary"
                        sx={{ fontWeight: 600, fontSize: '0.9rem' }}
                      />
                      {creator?.status === "Counter" && (
                        <Chip
                          label="Counter Offer Pending"
                          color="warning"
                          sx={{ fontWeight: 600, fontSize: '0.9rem' }}
                        />
                      )}
                    </Box>
                  </Box>
                </Card>

                {/* Campaign Brief */}
                <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 2,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: theme.palette.action.hover },
                    }}
                    onClick={() => toggleSection('brief')}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: isMobile ? '1.1rem' : '1.25rem' }}>
                      Campaign Brief
                    </Typography>
                    <IconButton
                      size="small"
                      sx={{
                        transform: expandedSections.brief ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s',
                      }}
                    >
                      <ExpandMoreIcon />
                    </IconButton>
                  </Box>
                  <Collapse in={expandedSections.brief}>
                    <Divider />
                    <Box sx={{ p: 2.5, bgcolor: theme.palette.background.paper }}>
                      <Typography
                        sx={{
                          whiteSpace: "pre-wrap",
                          lineHeight: 1.8,
                          fontSize: isMobile ? '0.95rem' : '1rem',
                          color: theme.palette.text.primary,
                        }}
                      >
                        {renderBriefWithLinks(campaign.brief)}
                      </Typography>
                    </Box>
                  </Collapse>
                </Card>

                {/* Promotions & Crossposts */}
                <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 2,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: theme.palette.action.hover },
                    }}
                    onClick={() => toggleSection('promotions')}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: isMobile ? '1.1rem' : '1.25rem' }}>
                      Deliverables ({videoList.length + crosspostList.length})
                    </Typography>
                    <IconButton
                      size="small"
                      sx={{
                        transform: expandedSections.promotions ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s',
                      }}
                    >
                      <ExpandMoreIcon />
                    </IconButton>
                  </Box>
                  <Collapse in={expandedSections.promotions}>
                    <Divider />
                    <Box sx={{ p: 2.5, bgcolor: theme.palette.background.paper }}>
                      {videoList.length > 0 && (
                        <Box sx={{ mb: crosspostList.length > 0 ? 2.5 : 0 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: 'text.secondary' }}>
                            Primary Promotions
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {videoList.map((v) => (
                              <Chip
                                key={v.id}
                                label={`${v.promotionPlatform} - ${v.promotionType}`}
                                color="primary"
                                variant="outlined"
                                sx={{ fontWeight: 600, mb: 1 }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}
                      {crosspostList.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: 'text.secondary' }}>
                            Crossposts
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {crosspostList.map((v) => (
                              <Chip
                                key={v.id}
                                label={v.promotionPlatform}
                                color="secondary"
                                variant="outlined"
                                sx={{ fontWeight: 600, mb: 1 }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Box>
                  </Collapse>
                </Card>

                {/* Creator Brief */}
                {creator?.creatorBrief && (
                  <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: theme.palette.action.hover },
                      }}
                      onClick={() => toggleSection('creatorBrief')}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: isMobile ? '1.1rem' : '1.25rem' }}>
                        Your Creator Brief
                      </Typography>
                      <IconButton
                        size="small"
                        sx={{
                          transform: expandedSections.creatorBrief ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s',
                        }}
                      >
                        <ExpandMoreIcon />
                      </IconButton>
                    </Box>
                    <Collapse in={expandedSections.creatorBrief}>
                      <Divider />
                      <Box sx={{ p: 2.5, bgcolor: theme.palette.grey[50] }}>
                        <Typography
                          sx={{
                            whiteSpace: "pre-wrap",
                            fontSize: isMobile ? '0.9rem' : '1rem',
                            lineHeight: 1.7,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {renderBriefWithLinks(creator.creatorBrief)}
                        </Typography>
                      </Box>
                    </Collapse>
                  </Card>
                )}

                {/* Action Buttons - Now in content area to ensure visibility */}
                <Card
                  elevation={3}
                  sx={{
                    border: `2px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    mt: 2,
                    position: 'sticky',
                    bottom: isMobile ? 16 : 0,
                    zIndex: 5,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box sx={{ p: 2.5 }}>
                    <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 700, textAlign: 'center' }}>
                      {creator?.status === "Counter"
                        ? "Waiting for the brand"
                        : "What would you like to do?"}
                    </Typography>

                    {creator?.status === "Counter" && (
                      <Stack spacing={1} sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                          Your counter offer is with the brand. You can accept the campaign only after they accept your
                          counter or send a new offer.
                        </Typography>
                        <Typography variant="body2" fontWeight={600} textAlign="center">
                          Brand&apos;s last offer:{" "}
                          {currentRate ? `$${Number(currentRate).toFixed(2)}` : "—"}
                        </Typography>
                        {pendingCounterAmount != null && (
                          <Typography variant="body2" fontWeight={600} textAlign="center" color="warning.main">
                            Your counter: ${Number(pendingCounterAmount).toFixed(2)}
                          </Typography>
                        )}
                      </Stack>
                    )}

                    <Stack spacing={2}>
                      {/* Primary Actions - Accept only when brand offer is on the table (Pitched / unset), not while Counter */}
                      {creator?.status !== "Counter" ? (
                        <Stack spacing={2}>
                          <Button
                            variant="contained"
                            color="success"
                            onClick={() => onClickOption(true)}
                            fullWidth
                            size="large"
                            sx={{
                              py: 1.75,
                              fontSize: '1.05rem',
                              fontWeight: 700,
                              textTransform: 'none',
                              borderRadius: 1.5,
                              boxShadow: 3,
                              '&:hover': {
                                boxShadow: 6,
                                transform: 'translateY(-2px)',
                              },
                              transition: 'all 0.2s',
                            }}
                          >
                            ✓ Accept Campaign
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => onClickOption(false)}
                            fullWidth
                            size="large"
                            sx={{
                              py: 1.75,
                              fontSize: '1.05rem',
                              fontWeight: 700,
                              textTransform: 'none',
                              borderRadius: 1.5,
                              borderWidth: 2,
                              '&:hover': {
                                borderWidth: 2,
                                transform: 'translateY(-2px)',
                              },
                              transition: 'all 0.2s',
                            }}
                          >
                            ✕ Decline Campaign
                          </Button>
                        </Stack>
                      ) : (
                        <Stack spacing={2}>
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => onClickOption(false)}
                            fullWidth
                            size="large"
                            sx={{
                              py: 1.75,
                              fontSize: '1.05rem',
                              fontWeight: 700,
                              textTransform: 'none',
                              borderRadius: 1.5,
                              borderWidth: 2,
                              '&:hover': {
                                borderWidth: 2,
                                transform: 'translateY(-2px)',
                              },
                              transition: 'all 0.2s',
                            }}
                          >
                            ✕ Decline Campaign
                          </Button>
                        </Stack>
                      )}

                      <Divider sx={{ my: 1 }} />

                      {/* Secondary Actions */}
                      <Stack spacing={1.5}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => setOpenChatPopup(true)}
                          fullWidth
                          size="large"
                          sx={{
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: 1.5,
                          }}
                        >
                          💬 Chat with Brand
                        </Button>
                        {creator?.status !== "Counter" && (
                          <Button
                            variant="contained"
                            color="warning"
                            onClick={() => {
                              setOpenNegotiateDialog(true);
                              navigate("/creatorconnect/collabs", { replace: true });
                            }}
                            fullWidth
                            size="large"
                            sx={{
                              py: 1.5,
                              fontSize: '1rem',
                              fontWeight: 600,
                              textTransform: 'none',
                              borderRadius: 1.5,
                            }}
                          >
                            💰 Negotiate Rate
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                  </Box>
                </Card>
              </Stack>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Negotiate Dialog */}
      <CreatorNegotiateDialog
        open={openNegotiateDialog}
        handleClose={() => setOpenNegotiateDialog(false)}
        campaignId={campaignId}
        currentRate={currentRate}
        refreshCampaign={refreshCampaign}
        onSuccess={handleCloseDialog}
      />

      {/* Chat Dialog */}
      <Dialog
        open={openChatPopup}
        onClose={() => setOpenChatPopup(false)}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            height: isMobile ? '100vh' : '75vh',
            margin: isMobile ? 0 : 2,
            borderRadius: isMobile ? 0 : 2,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }
        }}
        PaperProps={{
          elevation: isMobile ? 0 : 8,
          sx: {
            backgroundColor: theme.palette.background.default,
          }
        }}
      >
        <Paper
          elevation={0}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            borderRadius: 0,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Chat with Brand
            </Typography>
            <IconButton onClick={() => setOpenChatPopup(false)} sx={{ color: 'white' }} size="large">
              <CloseIcon />
            </IconButton>
          </Box>
        </Paper>

        <DialogContent
          sx={{
            p: 0,
            display: "flex",
            flexDirection: "column",
            flex: 1,
            overflow: 'hidden',
            backgroundColor: theme.palette.background.default,
          }}
        >
          <CreatorCampaignConversation campaignId={campaignId} />
        </DialogContent>
      </Dialog>

      <AlertDialog alertState={dialogState} />
    </>
  );
}